import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Eye, EyeOff, Pencil, Plus } from 'lucide-react';
import { adminApi } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

type ServerForm = {
  id?: number;
  slug: string;
  name: string;
  rconHost: string;
  rconPort: number;
  rconPassword: string;
  whitelistAddCmd: string;
  whitelistRemoveCmd: string;
  pluginToken: string;
  isEnabled: boolean;
  sortOrder: number;
};

const empty = (): ServerForm => ({
  slug: '',
  name: '',
  rconHost: '127.0.0.1',
  rconPort: 25575,
  rconPassword: '',
  whitelistAddCmd: 'whitelist add {nickname}',
  whitelistRemoveCmd: 'whitelist remove {nickname}',
  pluginToken: crypto.randomUUID().replaceAll('-', ''),
  isEnabled: true,
  sortOrder: 0,
});

export function AdminServers() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'servers'], queryFn: adminApi.servers.list });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ServerForm>(empty());

  const mut = useMutation({
    mutationFn: () => (form.id ? adminApi.servers.update(form.id, form) : adminApi.servers.create(form)),
    onSuccess: () => {
      toast.success('Сохранено');
      qc.invalidateQueries({ queryKey: ['admin', 'servers'] });
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const toggleMut = useMutation({
    mutationFn: (id: number) => adminApi.servers.toggle(id),
    onSuccess: (r) => {
      toast.success(r.isEnabled ? 'Включён' : 'Отключён');
      qc.invalidateQueries({ queryKey: ['admin', 'servers'] });
    },
    onError: () => toast.error('Не удалось переключить'),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">Серверы</h1>
        <Button onClick={() => { setForm(empty()); setOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Создать
        </Button>
      </div>

      <div className="space-y-3">
        {(data as any[])?.map((s) => (
          <Card key={s.id}>
            <CardContent className="pt-4 flex items-center justify-between flex-wrap gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-eol-white">{s.name}</h3>
                  <Badge variant="secondary">ID: {s.id}</Badge>
                  <Badge variant="secondary">{s.slug}</Badge>
                  {!s.isEnabled && <Badge variant="danger">Выключен</Badge>}
                </div>
                <p className="text-xs text-eol-desc mt-1">{s.rconHost}:{s.rconPort}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(String(s.id));
                      toast.success(`ID ${s.id} скопирован`);
                    }}
                    className="inline-flex items-center gap-1 text-xs text-eol-desc hover:text-eol-pink"
                    title="Скопировать ID"
                  >
                    <Copy className="h-3 w-3" /> server-id
                  </button>
                  {s.pluginToken && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(String(s.pluginToken));
                        toast.success('Токен плагина скопирован');
                      }}
                      className="inline-flex items-center gap-1 text-xs text-eol-desc hover:text-eol-pink"
                      title="Скопировать токен"
                    >
                      <Copy className="h-3 w-3" /> bridge-token
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="secondary"
                  onClick={() => { setForm({ ...s }); setOpen(true); }}
                  title="Редактировать"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => toggleMut.mutate(s.id)}
                  title={s.isEnabled ? 'Отключить' : 'Включить'}
                  disabled={toggleMut.isPending}
                >
                  {s.isEnabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 opacity-50" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Сервер' : 'Новый сервер'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Код (латиница, напр. main)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            <Input placeholder="Название" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="RCON-хост" value={form.rconHost} onChange={(e) => setForm({ ...form, rconHost: e.target.value })} />
            <Input type="number" placeholder="RCON-порт" value={form.rconPort} onChange={(e) => setForm({ ...form, rconPort: Number(e.target.value) })} />
            <Input placeholder="RCON-пароль" type="password" value={form.rconPassword} onChange={(e) => setForm({ ...form, rconPassword: e.target.value })} className="col-span-2" />
            <Input placeholder="Команда добавления в whitelist" value={form.whitelistAddCmd} onChange={(e) => setForm({ ...form, whitelistAddCmd: e.target.value })} className="col-span-2" />
            <Input placeholder="Команда удаления из whitelist" value={form.whitelistRemoveCmd} onChange={(e) => setForm({ ...form, whitelistRemoveCmd: e.target.value })} className="col-span-2" />
            <div className="col-span-2">
              <p className="text-xs text-eol-desc mb-1">
                Токен для плагина EOLShop и fallback-моста (если RCON недоступен).
                Этот же токен указываешь в <code className="text-eol-pink">plugins/EOLShop/config.yml</code> → <code className="text-eol-pink">bridge-token</code>.
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="Токен"
                  value={form.pluginToken}
                  onChange={(e) => setForm({ ...form, pluginToken: e.target.value })}
                  className="flex-1 font-mono text-xs"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    setForm({ ...form, pluginToken: crypto.randomUUID().replaceAll('-', '') })
                  }
                >
                  Сгенерировать
                </Button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-eol-white col-span-2">
              <input type="checkbox" checked={form.isEnabled} onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })} />
              Включён
            </label>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
            <Button onClick={() => mut.mutate()} disabled={mut.isPending}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
